import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import type { SimpleEvent } from '@passionware/simple-event';
import { sortBy, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import type { Resolver } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import type { RootStore } from 'store/reducer';
import { getTags, selectTagsStore } from 'store/v1/tags';
import type { OptionRef } from 'v5/core/entity-values/values.types';
import { useExclusiveEdit } from 'v5/patterns/forms/exclusive-edit/ExclusiveEditContext';
import {
  TagsField,
  type TagsFieldSpec
} from 'v5/patterns/forms/schema-form/fields/TagsField';
import type { UnitTarget } from 'v5/patterns/forms/schema-form/schemaForm.types';
import { UnitEditor } from 'v5/patterns/forms/schema-form/UnitEditor';
import { useLockPinViewerNavigation } from 'v5/patterns/widgets/PinViewer/pinViewerNavigationLock';

/** A tag as a pin carries it; one picked by name and not saved yet has no id. */
export type PinTag = { id?: number; tag: string };

type Values = { tags: OptionRef[] };

const unit = {
  id: 'pin-tags',
  title: 'Tags',
  editability: { type: 'editable' }
} as const;

const readOnlyUnit = {
  ...unit,
  editability: {
    type: 'read-only',
    reason: 'Tags are changed by those who can update the resource.'
  }
} as const;

const noValidation: Resolver<Values> = async values => ({ values, errors: {} });

/** Every tag of the workspace, from the v1 store, fetched when missing: what a pin's tags are picked from. */
export function usePinTagCatalog(): PinTag[] {
  const dispatch =
    useDispatch<ThunkDispatch<RootStore, undefined, AnyAction>>();
  const tagsStore = useSelector(selectTagsStore);
  useEffect(() => {
    if (!tagsStore.fetched) void dispatch(getTags({}));
  }, [dispatch, tagsStore.fetched]);
  return useMemo(() => Object.values(tagsStore.data), [tagsStore.data]);
}

/** Renders nothing: keeps the viewer on this pin, open, while its tags are edited. */
function LockPinViewerNavigation({ locked }: { locked: boolean }) {
  const exclusiveEdit = useExclusiveEdit();
  const requestLeave = useCallback(
    () => exclusiveEdit.requestEdit(() => {}),
    [exclusiveEdit]
  );
  useLockPinViewerNavigation(locked, requestLeave);
  return null;
}

/** The unit's id, for an `editUnitSignal` target. */
export const PIN_TAGS_UNIT_ID = unit.id;

/**
 * The tags of one pin as an editable unit, the way the resource pane shows
 * its Tags unit: chips at rest, the combobox with Save / Cancel after the
 * pencil. `onSave` gets the whole list of tags, in the order picked; edit mode
 * waits for what it returns. While it is in edit mode the surrounding
 * `PinViewer` stays on this pin and open. Needs an `ExclusiveEditProvider`
 * around it.
 */
export function PinTagsUnit({
  tags,
  catalog,
  onSave,
  editUnitSignal,
  readOnly = false,
  allowCreate
}: {
  tags: PinTag[];
  /** What can be picked; the pin's own tags are offered whether or not they are in it. */
  catalog: PinTag[];
  onSave(tags: PinTag[]): void | Promise<unknown>;
  /** A target with `PIN_TAGS_UNIT_ID` enters edit mode, as the pencil does. */
  editUnitSignal?: SimpleEvent<UnitTarget>;
  /** The tags show without the pencil. */
  readOnly?: boolean;
  /** Offers "Create new tag" for a typed name. */
  allowCreate: boolean;
}) {
  const known = useMemo(
    () =>
      sortBy(
        uniqBy(
          [...tags, ...catalog].filter(tag => tag.id !== undefined),
          tag => tag.id
        ),
        tag => tag.tag.toLowerCase()
      ),
    [tags, catalog]
  );
  const spec = useMemo<TagsFieldSpec>(
    () => ({
      key: 'tags',
      label: unit.title,
      description: null,
      required: false,
      // A new name goes in the pin's update without an id, as v1 sent it.
      allowCreate,
      options: known.map(tag => ({ value: String(tag.id), label: tag.tag }))
    }),
    [known, allowCreate]
  );
  const formValues = useMemo<Values>(
    () => ({
      tags: tags.flatMap(tag =>
        tag.id === undefined ? [] : [{ type: 'existing', id: tag.id }]
      )
    }),
    [tags]
  );

  return (
    <UnitEditor<Values>
      unit={readOnly ? readOnlyUnit : unit}
      formValues={formValues}
      resolver={noValidation}
      onSave={async data => {
        await onSave(
          data.tags.flatMap(ref =>
            ref.type === 'new'
              ? [{ tag: ref.label }]
              : known.filter(tag => tag.id === ref.id)
          )
        );
      }}
      editUnitSignal={editUnitSignal}
      content={<TagsField spec={spec} path="tags" hideLabel />}
      // The title is told whether the unit is in edit mode, and goes with it.
      titleExtras={({ isEditing }) => (
        <LockPinViewerNavigation locked={isEditing} />
      )}
      required={false}
      groupTitle={false}
      variant="plain"
      size="sm"
    />
  );
}
